// src/views/folders/slice/actions.ts
import { AxiosError } from 'axios';
import { message } from 'antd';
import slice from './slice';
import { ApiError } from '../../../services/ApiService.types';
import { AppThunk } from '../../../redux/store';
import API from '../../../services/ApiService';
import { normalizeTreeData } from '../../../utils/dateNormalization';
import { polishServerText } from '../../../utils/apiErrors';
import i18n from '../../../i18n';

const {
  startLoadingTree,
  setTreeData,
  setTreeError,
  startCreatingFolder,
  finishCreatingFolder,
  setCreateFolderError,
  startDeletingFolder,
  finishDeletingFolder,
  setDeleteFolderError,
  startMovingItem,
  finishMovingItem,
  setMoveItemError,
  startRenamingItem,
  finishRenamingItem,
  setRenameItemError,
  toggleFolder,
  setSelectedItem,
  expandAllFolders,
  collapseAllFolders,
  setCurrentFolder,
  setBreadcrumbs,
  setSearchQuery,
  startSearching,
  setSearchResults,
  setStatistics,
  setViewMode,
  setSortBy,
  setSortOrder,
  setTreeComplete,
  setNeedsRefresh,
  setFolderChildren,
  startLoadingFolder,
  finishLoadingFolder,
} = slice.actions;

const CACHE_DURATION = 5 * 60 * 1000;

const isCacheValid = (state: any): boolean => {
  const { cacheTimestamp, needsRefresh } = state.folders;
  if (needsRefresh) return false;
  if (!cacheTimestamp) return false;

  return Date.now() - cacheTimestamp < CACHE_DURATION;
};

// ✅ KLUCZ 1: Używamy Redux state zamiast globalnej zmiennej
const fetchFolderTreeWithLists = (forceRefresh = false): AppThunk => async (dispatch, getState) => {
  const state = getState();

  // ✅ Sprawdź czy już trwa ładowanie (z Redux, nie z globalnej zmiennej)
  if (state.folders.isLoadingTree && !forceRefresh) {
    console.log('🔄 Already loading tree, skipping...');

    return;
  }

  // ✅ Jeśli forceRefresh=true, zawsze pobierz nowe dane
  if (!forceRefresh && isCacheValid(state) && state.folders.folderTreeWithLists.length > 0) {
    console.log('✅ Using cached tree data');

    return;
  }

  console.log('🔄 Fetching fresh tree data...');
  dispatch(startLoadingTree());

  try {
    const { data } = await API.folders.getTreeWithLists();
    const normalizedData = normalizeTreeData(data.items);

    dispatch(setTreeComplete({
      data: normalizedData,
      timestamp: Date.now(),
    }));

    console.log('✅ Tree data refreshed successfully');
  } catch (e) {
    const { response } = e as AxiosError<ApiError>;
    dispatch(setTreeError(response?.data?.message || 'Failed to load folder tree'));
    message.error(i18n.t('folders:messages.treeLoadError'));
  }
};

// ✅ KLUCZ 2: Invaliduj cache PRZED każdą operacją
const invalidateCache = (): AppThunk => (dispatch) => {
  console.log('🗑️ Cache invalidated');
  dispatch(setNeedsRefresh(true));
};

const createFolder = (name: string, parentFolderId?: number): AppThunk => async (dispatch) => {
  dispatch(startCreatingFolder());

  try {
    await API.folders.create({ name, parentFolderId });

    // ✅ KLUCZ 3: Invaliduj i odśwież
    dispatch(invalidateCache());
    await dispatch(fetchFolderTreeWithLists(true));

    message.success(i18n.t('folders:messages.folderCreated'));
  } catch (e) {
    const { response } = e as AxiosError<ApiError>;
    dispatch(setCreateFolderError(response?.data?.message || 'Failed to create folder'));
    message.error(i18n.t('folders:messages.createFolderError'));
  } finally {
    dispatch(finishCreatingFolder());
  }
};

const renameItem = (id: string, name: string): AppThunk => async (dispatch) => {
  dispatch(startRenamingItem());

  try {
    const isFolder = id.startsWith('folder-');
    const itemId = parseInt(id.replace(isFolder ? 'folder-' : 'list-', ''), 10);

    if (isFolder) {
      await API.folders.rename(itemId, name);
    } else {
      // ✅ Pobierz pełne dane listy
      const { data: listData } = await API.wordsList.getOne(itemId.toString());

      // ✅ Zapisz z bezpiecznymi fallbackami
      await API.wordsList.save(itemId.toString(), {
        name,
        description: (listData as any).description || '',
        words: listData.words || [],
      });
    }

    // ✅ Invaliduj i odśwież
    dispatch(invalidateCache());
    await dispatch(fetchFolderTreeWithLists(true));

    message.success(i18n.t(isFolder ? 'folders:messages.folderRenamed' : 'folders:messages.listRenamed'));
  } catch (e) {
    const { response } = e as AxiosError<ApiError>;
    const errorMsg = polishServerText(response?.data?.message) || i18n.t<string>('folders:messages.renameError');
    dispatch(setRenameItemError(errorMsg));
    message.error(errorMsg);
  } finally {
    dispatch(finishRenamingItem());
  }
};

const deleteFolder = (id: number): AppThunk => async (dispatch) => {
  dispatch(startDeletingFolder());

  try {
    await API.folders.delete(id);

    // ✅ Invaliduj i odśwież
    dispatch(invalidateCache());
    await dispatch(fetchFolderTreeWithLists(true));

    message.success(i18n.t('folders:messages.folderDeleted'));
  } catch (e) {
    const { response } = e as AxiosError<ApiError>;
    dispatch(setDeleteFolderError(response?.data?.message || 'Failed to delete folder'));
    message.error(i18n.t('folders:messages.folderDeleteError'));
  } finally {
    dispatch(finishDeletingFolder());
  }
};

const moveItem = (
  itemId: number,
  itemType: 'folder' | 'list',
  targetFolderId: number | null,
  position?: number,
): AppThunk => async (dispatch) => {
  dispatch(startMovingItem());

  try {
    await API.folders.move({
      itemId,
      itemType,
      targetFolderId,
      position,
    });

    // ✅ Invaliduj i odśwież
    dispatch(invalidateCache());
    await dispatch(fetchFolderTreeWithLists(true));
  } catch (e) {
    const { response } = e as AxiosError<ApiError>;
    dispatch(setMoveItemError(response?.data?.message || 'Failed to move item'));
    message.error(i18n.t('folders:messages.itemMoveError'));
  } finally {
    dispatch(finishMovingItem());
  }
};

const bulkDeleteItems = (
  items: Array<{ id: string; type: 'folder' | 'list' }>,
): AppThunk => async (dispatch) => {
  dispatch(startDeletingFolder());

  try {
    const formattedItems = items.map((item) => ({
      id: parseInt(item.id.replace(`${item.type}-`, ''), 10),
      type: item.type,
    }));

    const { data } = await API.folders.bulkDelete(formattedItems);

    if (data.errors.length > 0) {
      // Serwer pisze po polsku — przy innym języku ogólny tekst za każdy nieusunięty element
      data.errors.forEach((error: string) => (
        message.warning(polishServerText(error) || i18n.t('folders:messages.itemNotDeleted'))
      ));
    }

    // ✅ Invaliduj i odśwież
    dispatch(invalidateCache());
    await dispatch(fetchFolderTreeWithLists(true));
  } catch (e) {
    const { response } = e as AxiosError<ApiError>;
    message.error(polishServerText(response?.data?.message) || i18n.t('folders:messages.itemsDeleteError'));

    // ✅ Nawet w przypadku błędu odśwież, żeby pokazać aktualny stan
    dispatch(invalidateCache());
    await dispatch(fetchFolderTreeWithLists(true));
  } finally {
    dispatch(finishDeletingFolder());
  }
};

// ✅ KLUCZ 4: Uproszczona funkcja navigacji bez dodatkowego fetchowania
const navigateToFolder = (folderId: string | null): AppThunk => async (dispatch) => {
  dispatch(setCurrentFolder(folderId));

  if (folderId && folderId.startsWith('folder-')) {
    const id = parseInt(folderId.replace('folder-', ''), 10);
    try {
      const { data } = await API.folders.getBreadcrumbs(id);
      dispatch(setBreadcrumbs(data));
    } catch (e) {
      console.error('Failed to fetch breadcrumbs', e);
    }
  } else {
    dispatch(setBreadcrumbs([]));
  }
};

let searchTimeout: NodeJS.Timeout;
const searchItems = (query: string): AppThunk => async (dispatch) => {
  dispatch(setSearchQuery(query));

  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  if (!query.trim()) {
    dispatch(setSearchResults([]));

    return;
  }

  searchTimeout = setTimeout(async () => {
    dispatch(startSearching());
    try {
      const { data } = await API.folders.search(query);
      const normalizedResults = normalizeTreeData(data);
      dispatch(setSearchResults(normalizedResults));
    } catch (e) {
      console.error('Search failed', e);
      dispatch(setSearchResults([]));
    }
  }, 300);
};

const fetchStatistics = (): AppThunk => async (dispatch) => {
  try {
    const { data } = await API.folders.getStatistics();
    dispatch(setStatistics(data));
  } catch (e) {
    console.error('Failed to fetch statistics', e);
  }
};

const loadFolderChildren = (folderId: string): AppThunk => async (dispatch, getState) => {
  const state = getState();

  if (state.folders.loadedFolders.includes(folderId)) {
    return;
  }

  if (state.folders.loadingFolders.includes(folderId)) {
    return;
  }

  dispatch(startLoadingFolder(folderId));

  try {
    const { data } = await API.folders.getTreeWithLists();

    const findFolder = (nodes: any[]): any => {
      const found = nodes.find((node) => node.id === folderId);
      if (found) return found.children || [];

      return nodes.reduce((result: any, node) => {
        if (result) return result;
        if (node.children) {
          return findFolder(node.children);
        }

        return null;
      }, null) || [];
    };

    const children = findFolder(data.items);
    const normalizedChildren = normalizeTreeData(children);

    dispatch(setFolderChildren({ folderId, children: normalizedChildren }));
  } catch (error) {
    console.error('Failed to load folder children:', error);
    message.error(i18n.t('folders:messages.folderLoadError'));
    dispatch(finishLoadingFolder(folderId));
  }
};

const fetchFolderTree = (): AppThunk => async (dispatch) => {
  dispatch(startLoadingTree());
  try {
    const { data } = await API.folders.getTree();
    const normalizedData = normalizeTreeData(data.items);
    dispatch(setTreeData(normalizedData));
  } catch (e) {
    const { response } = e as AxiosError<ApiError>;
    dispatch(setTreeError(response?.data?.message || 'Failed to load folder tree'));
  }
};

export {
  fetchFolderTree,
  fetchFolderTreeWithLists,
  createFolder,
  renameItem,
  deleteFolder,
  moveItem,
  navigateToFolder,
  searchItems,
  fetchStatistics,
  toggleFolder,
  setSelectedItem,
  expandAllFolders,
  collapseAllFolders,
  setViewMode,
  setSortBy,
  setSortOrder,
  loadFolderChildren,
  bulkDeleteItems,
  invalidateCache,
};

// // src/views/folders/slice/actions.ts
// import { AxiosError } from 'axios';
// import { message } from 'antd';
// import slice from './slice';
// import { ApiError } from '../../../services/ApiService.types';
// import { AppThunk } from '../../../redux/store';
// import API from '../../../services/ApiService';
// import { normalizeTreeData } from '../../../utils/dateNormalization';

// const {
//   startLoadingTree,
//   setTreeData,
//   setTreeError,
//   startCreatingFolder,
//   finishCreatingFolder,
//   setCreateFolderError,
//   startDeletingFolder,
//   finishDeletingFolder,
//   setDeleteFolderError,
//   startMovingItem,
//   finishMovingItem,
//   setMoveItemError,
//   startRenamingItem,
//   finishRenamingItem,
//   setRenameItemError,
//   toggleFolder,
//   setSelectedItem,
//   expandAllFolders,
//   collapseAllFolders,
//   setCurrentFolder,
//   setBreadcrumbs,
//   setSearchQuery,
//   startSearching,
//   setSearchResults,
//   setStatistics,
//   setViewMode,
//   setSortBy,
//   setSortOrder,
//   setTreeComplete,
//   setNeedsRefresh,
//   setFolderChildren,
//   startLoadingFolder,
//   finishLoadingFolder,
// } = slice.actions;

// const CACHE_DURATION = 5 * 60 * 1000;

// const isCacheValid = (state: any): boolean => {
//   const { cacheTimestamp, needsRefresh } = state.folders;
//   if (needsRefresh) return false;
//   if (!cacheTimestamp) return false;

//   return Date.now() - cacheTimestamp < CACHE_DURATION;
// };

// let isFetchingTree = false;

// const fetchFolderTreeWithLists = (forceRefresh = false): AppThunk => async (dispatch, getState) => {
//   const state = getState();

//   if (isFetchingTree) {
//     return;
//   }

//   if (!forceRefresh && isCacheValid(state) && state.folders.folderTreeWithLists.length > 0) {
//     return;
//   }

//   isFetchingTree = true;
//   dispatch(startLoadingTree());

//   try {
//     const { data } = await API.folders.getTreeWithLists();
//     const normalizedData = normalizeTreeData(data.items);

//     dispatch(setTreeComplete({
//       data: normalizedData,
//       timestamp: Date.now(),
//     }));
//   } catch (e) {
//     const { response } = e as AxiosError<ApiError>;
//     dispatch(setTreeError(response?.data?.message || 'Failed to load folder tree'));
//     message.error('Nie udało się załadować struktury folderów');
//   } finally {
//     isFetchingTree = false;
//   }
// };

// const fetchFolderTree = (): AppThunk => async (dispatch) => {
//   dispatch(startLoadingTree());
//   try {
//     const { data } = await API.folders.getTree();
//     const normalizedData = normalizeTreeData(data.items);
//     dispatch(setTreeData(normalizedData));
//   } catch (e) {
//     const { response } = e as AxiosError<ApiError>;
//     dispatch(setTreeError(response?.data?.message || 'Failed to load folder tree'));
//   }
// };

// // ✅ UPROSZCZONE AKCJE - bez optimisticUpdate
// const createFolder = (name: string, parentFolderId?: number): AppThunk => async (dispatch) => {
//   dispatch(startCreatingFolder());

//   try {
//     await API.folders.create({ name, parentFolderId });

//     // ✅ Po prostu odśwież drzewo
//     await dispatch(fetchFolderTreeWithLists(true));

//     message.success('Folder został utworzony');
//   } catch (e) {
//     const { response } = e as AxiosError<ApiError>;
//     dispatch(setCreateFolderError(response?.data?.message || 'Failed to create folder'));
//     message.error('Nie udało się utworzyć folderu');
//   } finally {
//     dispatch(finishCreatingFolder());
//   }
// };

// const renameItem = (id: string, name: string): AppThunk => async (dispatch) => {
//   dispatch(startRenamingItem());

//   try {
//     const isFolder = id.startsWith('folder-');
//     const itemId = parseInt(id.replace(isFolder ? 'folder-' : 'list-', ''), 10);

//     if (isFolder) {
//       await API.folders.rename(itemId, name);
//     } else {
//       const { data: listData } = await API.wordsList.getOne(itemId.toString());
//       await API.wordsList.save(itemId.toString(), {
//         ...listData,
//         name,
//         description: '',
//       });
//     }

//     // ✅ Odśwież drzewo
//     await dispatch(fetchFolderTreeWithLists(true));

//     message.success(`${isFolder ? 'Folder' : 'Lista'} została pomyślnie przemianowana`);
//   } catch (e) {
//     const { response } = e as AxiosError<ApiError>;
//     const errorMsg = response?.data?.message || 'Nie udało się zmienić nazwy';
//     dispatch(setRenameItemError(errorMsg));
//     message.error(errorMsg);
//   } finally {
//     dispatch(finishRenamingItem());
//   }
// };

// const deleteFolder = (id: number): AppThunk => async (dispatch) => {
//   dispatch(startDeletingFolder());

//   try {
//     await API.folders.delete(id);

//     // ✅ Odśwież drzewo
//     await dispatch(fetchFolderTreeWithLists(true));

//     message.success('Folder został usunięty');
//   } catch (e) {
//     const { response } = e as AxiosError<ApiError>;
//     dispatch(setDeleteFolderError(response?.data?.message || 'Failed to delete folder'));
//     message.error('Nie udało się usunąć folderu');
//   } finally {
//     dispatch(finishDeletingFolder());
//   }
// };

// const moveItem = (
//   itemId: number,
//   itemType: 'folder' | 'list',
//   targetFolderId: number | null,
//   position?: number,
// ): AppThunk => async (dispatch) => {
//   dispatch(startMovingItem());

//   try {
//     await API.folders.move({
//       itemId,
//       itemType,
//       targetFolderId,
//       position,
//     });

//     // ✅ Odśwież drzewo
//     await dispatch(fetchFolderTreeWithLists(true));
//   } catch (e) {
//     const { response } = e as AxiosError<ApiError>;
//     dispatch(setMoveItemError(response?.data?.message || 'Failed to move item'));
//     message.error('Nie udało się przenieść elementu');
//   } finally {
//     dispatch(finishMovingItem());
//   }
// };

// const navigateToFolder = (folderId: string | null): AppThunk => async (dispatch) => {
//   dispatch(setCurrentFolder(folderId));

//   if (folderId && folderId.startsWith('folder-')) {
//     const id = parseInt(folderId.replace('folder-', ''), 10);
//     try {
//       const { data } = await API.folders.getBreadcrumbs(id);
//       dispatch(setBreadcrumbs(data));
//     } catch (e) {
//       console.error('Failed to fetch breadcrumbs', e);
//     }
//   } else {
//     dispatch(setBreadcrumbs([]));
//   }
// };

// let searchTimeout: NodeJS.Timeout;
// const searchItems = (query: string): AppThunk => async (dispatch) => {
//   dispatch(setSearchQuery(query));

//   if (searchTimeout) {
//     clearTimeout(searchTimeout);
//   }

//   if (!query.trim()) {
//     dispatch(setSearchResults([]));

//     return;
//   }

//   searchTimeout = setTimeout(async () => {
//     dispatch(startSearching());
//     try {
//       const { data } = await API.folders.search(query);
//       const normalizedResults = normalizeTreeData(data);
//       dispatch(setSearchResults(normalizedResults));
//     } catch (e) {
//       console.error('Search failed', e);
//       dispatch(setSearchResults([]));
//     }
//   }, 300);
// };

// const fetchStatistics = (): AppThunk => async (dispatch) => {
//   try {
//     const { data } = await API.folders.getStatistics();
//     dispatch(setStatistics(data));
//   } catch (e) {
//     console.error('Failed to fetch statistics', e);
//   }
// };

// export const bulkDeleteItems = (
//   items: Array<{ id: string; type: 'folder' | 'list' }>,
// ): AppThunk => async (dispatch) => {
//   dispatch(startDeletingFolder());

//   try {
//     const formattedItems = items.map((item) => ({
//       id: parseInt(item.id.replace(`${item.type}-`, ''), 10),
//       type: item.type,
//     }));

//     const { data } = await API.folders.bulkDelete(formattedItems);

//     if (data.errors.length > 0) {
//       data.errors.forEach((error: string) => message.warning(error));
//     }

//     // ✅ Odśwież drzewo
//     await dispatch(fetchFolderTreeWithLists(true));
//   } catch (e) {
//     const { response } = e as AxiosError<ApiError>;
//     message.error(response?.data?.message || 'Błąd podczas usuwania elementów');
//     await dispatch(fetchFolderTreeWithLists(true));
//   } finally {
//     dispatch(finishDeletingFolder());
//   }
// };

// const loadFolderChildren = (folderId: string): AppThunk => async (dispatch, getState) => {
//   const state = getState();

//   if (state.folders.loadedFolders.includes(folderId)) {
//     return;
//   }

//   if (state.folders.loadingFolders.includes(folderId)) {
//     return;
//   }

//   dispatch(startLoadingFolder(folderId));

//   try {
//     const { data } = await API.folders.getTreeWithLists();

//     const findFolder = (nodes: any[]): any => {
//       const found = nodes.find((node) => node.id === folderId);
//       if (found) return found.children || [];

//       return nodes.reduce((result: any, node) => {
//         if (result) return result;
//         if (node.children) {
//           return findFolder(node.children);
//         }

//         return null;
//       }, null) || [];
//     };

//     const children = findFolder(data.items);
//     const normalizedChildren = normalizeTreeData(children);

//     dispatch(setFolderChildren({ folderId, children: normalizedChildren }));
//   } catch (error) {
//     console.error('Failed to load folder children:', error);
//     message.error('Nie udało się załadować zawartości folderu');
//     dispatch(finishLoadingFolder(folderId));
//   }
// };

// export const invalidateCache = (): AppThunk => (dispatch) => {
//   dispatch(setNeedsRefresh(true));
// };

// export {
//   fetchFolderTree,
//   fetchFolderTreeWithLists,
//   createFolder,
//   renameItem,
//   deleteFolder,
//   moveItem,
//   navigateToFolder,
//   searchItems,
//   fetchStatistics,
//   toggleFolder,
//   setSelectedItem,
//   expandAllFolders,
//   collapseAllFolders,
//   setViewMode,
//   setSortBy,
//   setSortOrder,
//   loadFolderChildren,
// };

// /////////////////////////////////////////////////////////////
// // src/views/folders/slice/actions.ts
// import { AxiosError } from 'axios';
// import { message } from 'antd';
// import slice from './slice';
// import { ApiError } from '../../../services/ApiService.types';
// import { AppThunk } from '../../../redux/store';
// import API from '../../../services/ApiService';
// import { normalizeTreeData } from '../../../utils/dateNormalization';

// const {
//   startLoadingTree,
//   setTreeData,
//   // setTreeWithListsData,
//   finishLoadingTree,
//   setTreeError,
//   startCreatingFolder,
//   finishCreatingFolder,
//   setCreateFolderError,
//   startDeletingFolder,
//   finishDeletingFolder,
//   setDeleteFolderError,
//   startMovingItem,
//   finishMovingItem,
//   setMoveItemError,
//   startRenamingItem,
//   finishRenamingItem,
//   setRenameItemError,
//   toggleFolder,
//   setSelectedItem,
//   expandAllFolders,
//   collapseAllFolders,
//   setCurrentFolder,
//   setBreadcrumbs,
//   setSearchQuery,
//   startSearching,
//   setSearchResults,
//   setStatistics,
//   setViewMode,
//   setSortBy,
//   setSortOrder,
//   // setCacheTimestamp,
//   setTreeComplete,
//   setNeedsRefresh,
//   optimisticUpdate,
//   setFolderChildren,
//   startLoadingFolder,
//   finishLoadingFolder,
// } = slice.actions;

// const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// const isCacheValid = (state: any): boolean => {
//   const { cacheTimestamp, needsRefresh } = state.folders;
//   if (needsRefresh) return false;
//   if (!cacheTimestamp) return false;

//   return Date.now() - cacheTimestamp < CACHE_DURATION;
// };

// const findNodeInTree = (nodes: any[], nodeId: string): any | null => {
//   const found = nodes.find((node) => node.id === nodeId);
//   if (found) return found;

//   return nodes.reduce((result, node) => {
//     if (result) return result;
//     if (node.children) {
//       return findNodeInTree(node.children, nodeId);
//     }

//     return null;
//   }, null);
// };

// // const fetchFolderTreeWithLists = (forceRefresh = false): AppThunk => async (dispatch, getState) => {
// //   const state = getState();

// //   if (!forceRefresh && isCacheValid(state) && state.folders.folderTreeWithLists.length > 0) {
// //     return;
// //   }

// //   dispatch(startLoadingTree());
// //   try {
// //     const { data } = await API.folders.getTreeWithLists();

// //     const normalizedData = normalizeTreeData(data.items);

// //     dispatch(setTreeWithListsData(normalizedData));
// //     dispatch(setCacheTimestamp(Date.now()));
// //     dispatch(setNeedsRefresh(false));
// //   } catch (e) {
// //     const { response } = e as AxiosError<ApiError>;
// //     dispatch(setTreeError(response?.data?.message || 'Failed to load folder tree'));
// //     message.error('Nie udało się załadować struktury folderów');
// //   } finally {
// //     dispatch(finishLoadingTree());
// //   }
// // };

// // ✅ Dodaj na początku pliku, razem z innymi stałymi
// let isFetchingTree = false;

// const fetchFolderTreeWithLists = (forceRefresh = false): AppThunk => async (dispatch, getState) => {
//   const state = getState();

//   // ✅ DODAJ TEN GUARD
//   if (isFetchingTree) {
//     console.log('⏭️ Pomijam fetchFolderTreeWithLists - już w trakcie');

//     return;
//   }

//   if (!forceRefresh && isCacheValid(state) && state.folders.folderTreeWithLists.length > 0) {
//     return;
//   }

//   isFetchingTree = true; // ✅ Ustaw flagę
//   dispatch(startLoadingTree());

//   try {
//     const { data } = await API.folders.getTreeWithLists();
//     const normalizedData = normalizeTreeData(data.items);
//     // dispatch(setTreeWithListsData(normalizedData));
//     // dispatch(setCacheTimestamp(Date.now()));
//     // dispatch(setNeedsRefresh(false));
//     dispatch(setTreeComplete({
//       data: normalizedData,
//       timestamp: Date.now(),
//     }));
//   } catch (e) {
//     const { response } = e as AxiosError<ApiError>;
//     dispatch(setTreeError(response?.data?.message || 'Failed to load folder tree'));
//     message.error('Nie udało się załadować struktury folderów');
//   } finally {
//     // dispatch(finishLoadingTree());
//     isFetchingTree = false; // ✅ Resetuj flagę
//   }
// };

// const fetchFolderTree = (): AppThunk => async (dispatch) => {
//   dispatch(startLoadingTree());
//   try {
//     const { data } = await API.folders.getTree();

//     const normalizedData = normalizeTreeData(data.items);

//     dispatch(setTreeData(normalizedData));
//   } catch (e) {
//     const { response } = e as AxiosError<ApiError>;
//     dispatch(setTreeError(response?.data?.message || 'Failed to load folder tree'));
//   } finally {
//     dispatch(finishLoadingTree());
//   }
// };

// const loadFolderChildren = (folderId: string): AppThunk => async (dispatch, getState) => {
//   const state = getState();

//   if (state.folders.loadedFolders.includes(folderId)) {
//     return;
//   }

//   if (state.folders.loadingFolders.includes(folderId)) {
//     return;
//   }

//   dispatch(startLoadingFolder(folderId));

//   try {
//     const { data } = await API.folders.getTreeWithLists();

//     const findFolder = (nodes: any[]): any => {
//       const found = nodes.find((node) => node.id === folderId);
//       if (found) return found.children || [];

//       return nodes.reduce((result: any, node) => {
//         if (result) return result;
//         if (node.children) {
//           return findFolder(node.children);
//         }

//         return null;
//       }, null) || [];
//     };

//     const children = findFolder(data.items);

//     const normalizedChildren = normalizeTreeData(children);

//     dispatch(setFolderChildren({ folderId, children: normalizedChildren }));
//   } catch (error) {
//     console.error('Failed to load folder children:', error);
//     message.error('Nie udało się załadować zawartości folderu');
//     dispatch(finishLoadingFolder(folderId));
//   }
// };

// const createFolder = (name: string, parentFolderId?: number): AppThunk => async (dispatch) => {
//   dispatch(startCreatingFolder());

//   const tempId = `temp-folder-${Date.now()}`;
//   const tempFolder = {
//     id: tempId,
//     name,
//     type: 'folder' as const,
//     children: [],
//     parentId: parentFolderId ? `folder-${parentFolderId}` : null,
//     data: {
//       totalListsCount: 0,
//       directListsCount: 0,
//       subfoldersCount: 0,
//     },
//   };

//   dispatch(optimisticUpdate({
//     operation: 'create',
//     data: {
//       parentId: parentFolderId ? `folder-${parentFolderId}` : null,
//       item: tempFolder,
//     },
//   }));

//   try {
//     const { data } = await API.folders.create({ name, parentFolderId });

//     const realFolder = {
//       id: `folder-${data.id}`,
//       name: data.name,
//       type: 'folder' as const,
//       children: [],
//       parentId: parentFolderId ? `folder-${parentFolderId}` : null,
//       data: {
//         totalListsCount: 0,
//         directListsCount: 0,
//         subfoldersCount: 0,
//       },
//     };

//     dispatch(optimisticUpdate({
//       operation: 'delete',
//       data: { itemId: tempId },
//     }));
//     dispatch(optimisticUpdate({
//       operation: 'create',
//       data: {
//         parentId: parentFolderId ? `folder-${parentFolderId}` : null,
//         item: realFolder,
//       },
//     }));

//     message.success('Folder został utworzony');
//   } catch (e) {
//     const { response } = e as AxiosError<ApiError>;
//     dispatch(setCreateFolderError(response?.data?.message || 'Failed to create folder'));

//     dispatch(optimisticUpdate({
//       operation: 'delete',
//       data: { itemId: tempId },
//     }));

//     message.error('Nie udało się utworzyć folderu');
//   } finally {
//     dispatch(finishCreatingFolder());
//   }
// };

// const renameItem = (id: string, name: string): AppThunk => async (dispatch, getState) => {
//   dispatch(startRenamingItem());

//   const state = getState();
//   const currentNode = findNodeInTree(state.folders.folderTreeWithLists, id);

//   if (!currentNode) {
//     message.error('Nie znaleziono elementu do zmiany nazwy');
//     dispatch(finishRenamingItem());

//     return;
//   }

//   const oldName = currentNode.name;

//   dispatch(optimisticUpdate({
//     operation: 'update',
//     data: { itemId: id, changes: { name } },
//   }));

//   try {
//     const isFolder = id.startsWith('folder-');
//     const itemId = parseInt(id.replace(isFolder ? 'folder-' : 'list-', ''), 10);

//     if (isFolder) {
//       await API.folders.rename(itemId, name);
//     } else {
//       const { data: listData } = await API.wordsList.getOne(itemId.toString());
//       await API.wordsList.save(itemId.toString(), {
//         ...listData,
//         name,
//         description: '',
//       });
//     }

//     message.success(`${isFolder ? 'Folder' : 'Lista'} została pomyślnie przemianowana`);
//   } catch (e) {
//     const { response } = e as AxiosError<ApiError>;
//     const errorMsg = response?.data?.message || 'Nie udało się zmienić nazwy';
//     dispatch(setRenameItemError(errorMsg));

//     if (oldName) {
//       dispatch(optimisticUpdate({
//         operation: 'update',
//         data: { itemId: id, changes: { name: oldName } },
//       }));
//     }

//     message.error(errorMsg);
//   } finally {
//     dispatch(finishRenamingItem());
//   }
// };

// const deleteFolder = (id: number): AppThunk => async (dispatch, getState) => {
//   dispatch(startDeletingFolder());

//   const folderId = `folder-${id}`;
//   const state = getState();

//   const folderToDelete = findNodeInTree(state.folders.folderTreeWithLists, folderId);
//   const hasChildren = folderToDelete?.children && folderToDelete.children.length > 0;

//   dispatch(optimisticUpdate({
//     operation: 'delete',
//     data: {
//       itemId: folderId,
//       moveChildrenToRoot: hasChildren,
//     },
//   }));

//   try {
//     await API.folders.delete(id);
//     message.success('Folder został usunięty');

//     if (hasChildren) {
//       setTimeout(() => {
//         dispatch(fetchFolderTreeWithLists(true));
//       }, 500);
//     }
//   } catch (e) {
//     const { response } = e as AxiosError<ApiError>;
//     dispatch(setDeleteFolderError(response?.data?.message || 'Failed to delete folder'));

//     dispatch(fetchFolderTreeWithLists(true));

//     message.error('Nie udało się usunąć folderu');
//   } finally {
//     dispatch(finishDeletingFolder());
//   }
// };

// const moveItem = (
//   itemId: number,
//   itemType: 'folder' | 'list',
//   targetFolderId: number | null,
//   position?: number,
// ): AppThunk => async (dispatch) => {
//   dispatch(startMovingItem());

//   const itemIdStr = `${itemType}-${itemId}`;
//   const targetParentId = targetFolderId ? `folder-${targetFolderId}` : null;

//   dispatch(optimisticUpdate({
//     operation: 'move',
//     data: { itemId: itemIdStr, targetParentId },
//   }));

//   try {
//     await API.folders.move({
//       itemId,
//       itemType,
//       targetFolderId,
//       position,
//     });
//   } catch (e) {
//     const { response } = e as AxiosError<ApiError>;
//     dispatch(setMoveItemError(response?.data?.message || 'Failed to move item'));

//     dispatch(fetchFolderTreeWithLists(true));

//     message.error('Nie udało się przenieść elementu');
//   } finally {
//     dispatch(finishMovingItem());
//   }
// };

// const navigateToFolder = (folderId: string | null): AppThunk => async (dispatch) => {
//   dispatch(setCurrentFolder(folderId));

//   if (folderId && folderId.startsWith('folder-')) {
//     const id = parseInt(folderId.replace('folder-', ''), 10);
//     try {
//       const { data } = await API.folders.getBreadcrumbs(id);
//       dispatch(setBreadcrumbs(data));
//     } catch (e) {
//       console.error('Failed to fetch breadcrumbs', e);
//     }
//   } else {
//     dispatch(setBreadcrumbs([]));
//   }
// };

// let searchTimeout: NodeJS.Timeout;
// const searchItems = (query: string): AppThunk => async (dispatch) => {
//   dispatch(setSearchQuery(query));

//   if (searchTimeout) {
//     clearTimeout(searchTimeout);
//   }

//   if (!query.trim()) {
//     dispatch(setSearchResults([]));

//     return;
//   }

//   searchTimeout = setTimeout(async () => {
//     dispatch(startSearching());
//     try {
//       const { data } = await API.folders.search(query);

//       // ✅ FIX: Normalize search results
//       const normalizedResults = normalizeTreeData(data);

//       dispatch(setSearchResults(normalizedResults));
//     } catch (e) {
//       console.error('Search failed', e);
//       dispatch(setSearchResults([]));
//     }
//   }, 300);
// };

// const fetchStatistics = (): AppThunk => async (dispatch) => {
//   try {
//     const { data } = await API.folders.getStatistics();
//     dispatch(setStatistics(data));
//   } catch (e) {
//     console.error('Failed to fetch statistics', e);
//   }
// };

// export const bulkDeleteItems = (
//   items: Array<{ id: string; type: 'folder' | 'list' }>,
// ): AppThunk => async (dispatch) => {
//   dispatch(startDeletingFolder());

//   items.forEach((item) => {
//     dispatch(optimisticUpdate({
//       operation: 'delete',
//       data: { itemId: item.id },
//     }));
//   });

//   try {
//     const formattedItems = items.map((item) => ({
//       id: parseInt(item.id.replace(`${item.type}-`, ''), 10),
//       type: item.type,
//     }));

//     const { data } = await API.folders.bulkDelete(formattedItems);

//     if (data.deleted > 0) {
//       // message.success(`Usunięto ${data.deleted} elementów`);
//     }

//     if (data.errors.length > 0) {
//       data.errors.forEach((error: string) => message.warning(error));
//       dispatch(fetchFolderTreeWithLists(true));
//     }
//   } catch (e) {
//     const { response } = e as AxiosError<ApiError>;
//     dispatch(fetchFolderTreeWithLists(true));
//     message.error(response?.data?.message || 'Błąd podczas usuwania elementów');
//   } finally {
//     dispatch(finishDeletingFolder());
//   }
// };

// export const invalidateCache = (): AppThunk => (dispatch) => {
//   dispatch(setNeedsRefresh(true));
// };

// export {
//   fetchFolderTree,
//   fetchFolderTreeWithLists,
//   createFolder,
//   renameItem,
//   deleteFolder,
//   moveItem,
//   navigateToFolder,
//   searchItems,
//   fetchStatistics,
//   toggleFolder,
//   setSelectedItem,
//   expandAllFolders,
//   collapseAllFolders,
//   setViewMode,
//   setSortBy,
//   setSortOrder,
//   loadFolderChildren,
//   optimisticUpdate,
// };
